import { useCallback, useEffect, useRef, useState } from 'react';
import { X, FileText, Download, ImageOff } from 'lucide-react';
import api from '@/services/api';
import { ALLOWED_IMAGE_TYPES, formatFileSize, getAttachmentUrl, isDirectAttachmentPath, supportChatApi } from '@/services/supportChat';
import type { SupportMessage } from '@/services/supportChat';

interface FileAttachmentProps {
  msg: SupportMessage;
  anonymousId?: string;
  /** Max image thumbnail width class, e.g. 'max-w-[200px]' */
  maxImageClass?: string;
  /** Z-index class for the lightbox overlay, e.g. 'z-[200]' */
  overlayZClass?: string;
}

// A blob: URL runs on the app origin, so only raster images keep their type;
// anything else (an old .svg/.html upload) is forced to a plain download.
function toSafeBlob(data: Blob): Blob {
  return ALLOWED_IMAGE_TYPES.includes(data.type)
    ? data
    : new Blob([data], { type: 'application/octet-stream' });
}

// Refresh presigned R2 links this long before they expire.
const LINK_REFRESH_MARGIN_SECONDS = 60;

/**
 * Resolves the URL to render for an attachment.
 * - Direct-to-R2 uploads ('r2/...' paths): fetch a short-lived presigned URL via
 *   `attachment-link` (through the authenticated `api` client) and use it as-is —
 *   no blob download, since these files can be huge. The link is refreshed
 *   before it expires so a chat left open keeps working.
 * - Legacy uploads: fetch the file as a blob via `api` (carries the JWT) and
 *   expose it as an object URL. `enabled=false` defers this (files are only
 *   downloaded when clicked).
 */
function useAttachmentSrc(
  path: string,
  conversationId: number,
  anonymousId?: string,
  enabled = true
): { url: string | null; error: boolean; reload: () => void } {
  const isDirect = isDirectAttachmentPath(path);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [generation, setGeneration] = useState(0);
  const reload = useCallback(() => setGeneration((g) => g + 1), []);

  // A different attachment starts from a clean slate; a refresh of the same
  // one keeps showing the old URL until the new one arrives.
  useEffect(() => {
    setUrl(null);
    setError(false);
  }, [isDirect, path, conversationId, anonymousId]);

  useEffect(() => {
    if (!enabled) return undefined;
    let canceled = false;
    let objectUrl: string | null = null;
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;

    if (isDirect) {
      supportChatApi
        .getAttachmentLink(path, conversationId, anonymousId)
        .then((res) => {
          if (canceled) return;
          setError(false);
          setUrl(res.data.url);
          const refreshIn = Math.max(res.data.expires_in - LINK_REFRESH_MARGIN_SECONDS, 30);
          refreshTimer = setTimeout(() => setGeneration((g) => g + 1), refreshIn * 1000);
        })
        .catch(() => {
          if (!canceled) setError(true);
        });
    } else {
      const legacyUrl = getAttachmentUrl(path, conversationId, anonymousId);
      api
        .get(legacyUrl, { responseType: 'blob' })
        .then((res) => {
          if (canceled) return;
          objectUrl = URL.createObjectURL(toSafeBlob(res.data as Blob));
          setError(false);
          setUrl(objectUrl);
        })
        .catch(() => {
          if (!canceled) setError(true);
        });
    }

    return () => {
      canceled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [enabled, generation, isDirect, path, conversationId, anonymousId]);

  return { url, error, reload };
}

export default function FileAttachment({
  msg,
  anonymousId,
  maxImageClass = 'max-w-[250px] max-h-[260px]',
  overlayZClass = 'z-[100]',
}: FileAttachmentProps) {
  const [imageOpen, setImageOpen] = useState(false);

  if (!msg.attachment_url) return null;

  if (msg.message_type === 'image') {
    return (
      <ImageAttachment
        path={msg.attachment_url}
        conversationId={msg.conversation_id}
        anonymousId={anonymousId}
        alt={msg.attachment_name || 'image'}
        maxImageClass={maxImageClass}
        overlayZClass={overlayZClass}
        imageOpen={imageOpen}
        setImageOpen={setImageOpen}
      />
    );
  }

  return (
    <FileDownloadAttachment
      path={msg.attachment_url}
      conversationId={msg.conversation_id}
      anonymousId={anonymousId}
      name={msg.attachment_name}
      size={msg.attachment_size}
    />
  );
}

interface ImageAttachmentProps {
  path: string;
  conversationId: number;
  anonymousId?: string;
  alt: string;
  maxImageClass: string;
  overlayZClass: string;
  imageOpen: boolean;
  setImageOpen: (open: boolean) => void;
}

function ImageAttachment({
  path,
  conversationId,
  anonymousId,
  alt,
  maxImageClass,
  overlayZClass,
  imageOpen,
  setImageOpen,
}: ImageAttachmentProps) {
  const { url, error, reload } = useAttachmentSrc(path, conversationId, anonymousId);
  // An <img> that fails to load (e.g. a presigned link that expired before a
  // lazy image scrolled into view) fetches a fresh link once per attachment.
  // Keyed on the path, not the URL: every presigned URL is new, so a
  // permanently broken image would otherwise retry forever.
  const retriedPathRef = useRef<string | null>(null);
  const handleImageError = () => {
    if (!url || retriedPathRef.current === path || !isDirectAttachmentPath(path)) return;
    retriedPathRef.current = path;
    reload();
  };

  if (error) {
    return (
      <div
        className={`flex items-center gap-2 mt-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg ${maxImageClass}`}
      >
        <ImageOff className="w-4 h-4 text-gray-400 shrink-0" />
        <span className="text-xs text-gray-500 truncate">{alt}</span>
      </div>
    );
  }

  if (!url) {
    return (
      <div
        className={`mt-1 rounded-lg bg-gray-100 animate-pulse ${maxImageClass}`}
        style={{ aspectRatio: '4 / 3' }}
      />
    );
  }

  return (
    <>
      <button
        onClick={() => setImageOpen(true)}
        className="block mt-1 rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-amber-400"
      >
        <img
          src={url}
          alt={alt}
          className={`${maxImageClass} rounded-lg object-cover`}
          loading="lazy"
          onError={handleImageError}
        />
      </button>
      {imageOpen && (
        <div
          className={`fixed inset-0 ${overlayZClass} bg-black/70 flex items-center justify-center p-4`}
          onClick={() => setImageOpen(false)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setImageOpen(false)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow flex items-center justify-center hover:bg-gray-100 z-10"
            >
              <X className="w-4 h-4" />
            </button>
            <img
              src={url}
              alt={alt}
              className="max-w-[90vw] max-h-[90vh] rounded-lg object-contain"
            />
          </div>
        </div>
      )}
    </>
  );
}

interface FileDownloadAttachmentProps {
  path: string;
  conversationId: number;
  anonymousId?: string;
  name?: string;
  size?: number;
}

function FileDownloadAttachment({ path, conversationId, anonymousId, name, size }: FileDownloadAttachmentProps) {
  const isDirect = isDirectAttachmentPath(path);
  // Legacy files are downloaded through our server (rate limited), so they are
  // fetched only when clicked rather than for every file in the chat.
  const [requested, setRequested] = useState(isDirect);
  const { url, error, reload } = useAttachmentSrc(path, conversationId, anonymousId, requested);
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const clickWhenReadyRef = useRef(false);

  useEffect(() => {
    if (url && clickWhenReadyRef.current) {
      clickWhenReadyRef.current = false;
      anchorRef.current?.click();
    }
  }, [url]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (url) return;
    e.preventDefault();
    if (error) {
      // Retry a failed fetch (e.g. rate limited) on the next click.
      clickWhenReadyRef.current = !isDirect;
      reload();
    } else if (!isDirect && !requested) {
      clickWhenReadyRef.current = true;
      setRequested(true);
    }
  };

  const status = error
    ? '다운로드 실패'
    : !url && requested
      ? '불러오는 중...'
      : size !== undefined ? formatFileSize(size) : null;

  return (
    <a
      ref={anchorRef}
      href={url || '#'}
      download={name}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      aria-disabled={!url && requested}
      className={`flex items-center gap-2 mt-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg transition-colors max-w-[250px] ${
        url || !requested ? 'hover:bg-gray-100' : 'cursor-not-allowed opacity-60'
      }`}
    >
      <FileText className="w-5 h-5 text-gray-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-gray-700 truncate">{name}</div>
        {status && <div className="text-[10px] text-gray-400">{status}</div>}
      </div>
      <Download className="w-3.5 h-3.5 text-gray-400 shrink-0" />
    </a>
  );
}
